/**
 * Labstep
 *
 * @module state/reducers/prosemirror-referencing-search
 * @desc Reducers for ProseMirror referencing search
 */

import { setProseMirrorReferencingSearchTypeName } from 'labstep-web/state/actions/prosemirror-referencing-search';
import type { AnyAction } from 'redux';

/**
 * Interface for ProseMirror referencing search state
 */
export interface StateProseMirrorReferencingSearch {
  /** The search query string */
  query: string;
  /** Whether a search is currently in progress */
  isLoading: boolean;
  /** Search results array */
  results: ProseMirrorSearchResult[];
  /** Currently selected result index */
  selectedIndex: number;
  /** Type of entities being searched (protocols, experiments, resources, etc.) */
  entityType: string;
  /** Current workspace/group context for the search */
  groupId?: number;
  /** Error message if search fails */
  error?: string;
  /** Whether the search dropdown is open */
  isOpen: boolean;
  /** Pagination information */
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalResults: number;
    hasMore: boolean;
  };
}

/**
 * Individual search result item
 */
export interface ProseMirrorSearchResult {
  /** Unique identifier of the entity */
  id: number;
  /** Display name/title of the entity */
  name: string;
  /** Type of the entity (protocol, experiment, resource, etc.) */
  type: string;
  /** Optional description or preview text */
  description?: string;
  /** Custom identifier (e.g., SOP001, EXP123) */
  customId?: string;
  /** URL or path for linking to the entity */
  url?: string;
  /** Additional metadata for display */
  metadata?: {
    author?: string;
    createdAt?: string;
    updatedAt?: string;
    status: string;
  };
}

const proseMirrorReferencingSearch = (
  state: StateProseMirrorReferencingSearch | null = null,
  action: AnyAction,
): StateProseMirrorReferencingSearch | null => {
  if (
    action &&
    action.type === setProseMirrorReferencingSearchTypeName
  ) {
    return action.payload;
  }
  return state;
};

export default proseMirrorReferencingSearch;
